import { FunctionComponent, useCallback, useMemo } from "react";

import { faCalendarStar } from "@fortawesome/pro-regular-svg-icons";

import { extractPlaceholderVarNames } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { getSuggestionsMap } from "../../../List/utils/column-utils";

import { useEditorCursorInsertion } from "../AiSnippets/hooks/useEditorCursorInsertion";

import { VariablesSectionCard } from "./components/VariablesSectionCard";

import {
	buildVariableRows,
	buildVariableToken,
	collectNodeMessageContents,
	VariableRow,
} from "./utils/variableRows";

import type { List } from "../../../List/schema/list";

import type { MyNode } from "../../pages/CampaignMessagingOverview/CampaignMessagingOverview";
import type {
	EmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
	LinkedinInmailNodeConfig,
} from "../../../List/components/CampaignOverview/components/nodes";
import type { LinkedinVisitNodeConfig } from "../../../List/components/CampaignOverview/components/nodes/LinkedinVisitNode";

interface VariablesProps {
	listData?: List;
	selectedNode?:
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinInmailNodeConfig
		| LinkedinVisitNodeConfig;
	withPersistance?: (result: string[]) => void;
}

/**
 * "Variables" panel of the campaign node editor: lists every placeholder
 * available for the current lead together with its resolved value and lets
 * the user insert it at the editor cursor (click or drag).
 */
export const Variables: FunctionComponent<VariablesProps> = ({
	listData,
	selectedNode,
	withPersistance,
}) => {
	const { insertAtCursor, captureCurrentPosition } = useEditorCursorInsertion();

	const { inbox, currentLeadIndex } = useAppSelector(listSelector);

	const currentPerson = inbox[currentLeadIndex];

	const suggestionsMap = useMemo(
		() =>
			currentPerson
				? getSuggestionsMap(currentPerson, true, undefined, listData?.event)
				: getSuggestionsMap(undefined, true, listData?.columns, listData?.event),
		[currentPerson, listData?.columns, listData?.event]
	);

	const usedVarNames = useMemo(
		() => collectNodeMessageContents(selectedNode?.data).flatMap(extractPlaceholderVarNames),
		[selectedNode?.data]
	);

	const rows = useMemo(
		() =>
			buildVariableRows({
				suggestionsMap,
				currentPerson,
				listColumns: listData?.columns ?? currentPerson?.list?.columns,
				listEvent: listData?.event,
				usedVarNames,
				hasRealLead: Boolean(currentPerson),
			}),
		[suggestionsMap, currentPerson, listData?.columns, listData?.event, usedVarNames]
	);

	const defaultRows = useMemo(() => rows.filter((row) => row.category !== "event"), [rows]);
	const eventRows = useMemo(() => rows.filter((row) => row.category === "event"), [rows]);

	const defaultInUseCount = useMemo(
		() => defaultRows.filter((row) => row.isInUse).length,
		[defaultRows]
	);
	const eventInUseCount = useMemo(() => eventRows.filter((row) => row.isInUse).length, [eventRows]);

	const handleRowInsert = useCallback(
		(row: VariableRow) => {
			// Snapshot the caret of the editor that currently has focus (subject
			// or message) *before* inserting. The panel rows are non-focusable, so
			// clicking one never blurs the editor to refresh the shared
			// last-focused registry — without this the token lands in whichever
			// editor was blurred last (usually the message body). (PREC-3764)
			captureCurrentPosition();

			const token = buildVariableToken(row.key, row.label);

			if (insertAtCursor(token) || !withPersistance) {
				return;
			}

			// Fallback: append to the message when no editor was focused yet.
			const message = selectedNode?.data?.message ?? "";
			const next = message ? `${message} ${token}` : token;

			if (selectedNode?.type === "linkedinInviteNode") {
				// The pages map results[0] to premiumMessage for invite nodes.
				withPersistance([selectedNode?.data?.premiumMessage ?? "", next]);
			} else {
				withPersistance([next]);
			}
		},
		[captureCurrentPosition, insertAtCursor, withPersistance, selectedNode]
	);

	return (
		<div className="flex flex-col w-full mt-4 gap-3">
			<VariablesSectionCard
				inUseCount={defaultInUseCount}
				rows={defaultRows}
				onRowInsert={handleRowInsert}
			/>

			{eventRows.length > 0 && (
				<VariablesSectionCard
					emptySearchKey="variables.eventSection.emptySearch"
					icon={faCalendarStar}
					iconClassName="bg-purple-100 text-purple-800"
					inUseCount={eventInUseCount}
					rows={eventRows}
					searchPlaceholderKey="variables.eventSection.searchPlaceholder"
					titleKey="variables.eventSection.title"
					onRowInsert={handleRowInsert}
				/>
			)}
		</div>
	);
};
